import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Button, Typography, Paper, Avatar, Grid, Chip, Alert,
  CircularProgress, Fade, Collapse, Card, CardContent,
  useTheme, useMediaQuery, IconButton, Drawer, LinearProgress,
  Tabs, Tab, Tooltip,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import LinkIcon from '@mui/icons-material/Link';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddIcon from '@mui/icons-material/Add';
import TimerIcon from '@mui/icons-material/Timer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StarIcon from '@mui/icons-material/Star';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MenuIcon from '@mui/icons-material/Menu';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import LayersIcon from '@mui/icons-material/Layers';
import TopicIcon from '@mui/icons-material/Topic';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { AiActions } from '../../Services/_actions';
import { useKnowledgeMapSubjects } from '../Sidebar/ConceptBuilderSideBar.tsx';
import { useWebSocketAiContext } from '../../utils/WebSocketContext.tsx';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';
import QuestionCountSelector from '../Shared/QuestionSelect/index.tsx';
import StudentModal from '../PerformanceMap/kmap/StudentModal.tsx';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import { useSearchParams } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TestResult { testUrl?: string; responseText?: string; conversation_id?: string; }
interface PreloadedTest {
  _id: string; test_id: string; class: string; subject: string;
  test_type: string; test_mode?: string;
  total_questions: number; maximum_marks?: number; test_time: number;
  topics_covered: string[]; subtopics_covered?: string[];
  difficulty_distribution: { Easy?: number; Medium?: number; Hard?: number; hard?: number };
  test_started?: boolean; test_submitted?: boolean | null;
  score?: number;
}
type Step = 'testType' | 'topic' | 'subtopic' | 'level';
type ViewMode = 'browse' | 'custom' | 'testDetail';

// ─── Config ───────────────────────────────────────────────────────────────────
const SUBJECT_CFG: Record<string, { color: string; gradient: string; icon: string }> = {
  Physics: { color: '#3b82f6', gradient: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', icon: '⚛️' },
  Chemistry: { color: '#f59e0b', gradient: 'linear-gradient(135deg,#f59e0b,#d97706)', icon: '🧪' },
  Mathematics: { color: '#8b5cf6', gradient: 'linear-gradient(135deg,#8b5cf6,#6d28d9)', icon: '📐' },
  Biology: { color: '#10b981', gradient: 'linear-gradient(135deg,#10b981,#059669)', icon: '🧬' },
  English: { color: '#ef4444', gradient: 'linear-gradient(135deg,#ef4444,#dc2626)', icon: '📚' },
  History: { color: '#f97316', gradient: 'linear-gradient(135deg,#f97316,#ea580c)', icon: '🏛️' },
  Geography: { color: '#06b6d4', gradient: 'linear-gradient(135deg,#06b6d4,#0891b2)', icon: '🌍' },
  'Social Science': { color: '#ec4899', gradient: 'linear-gradient(135deg,#ec4899,#db2777)', icon: '🌐' },
};
const getSCfg = (s: string) => SUBJECT_CFG[s] || { color: '#6366f1', gradient: 'linear-gradient(135deg,#6366f1,#4f46e5)', icon: '📖' };
const CHIP_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#f97316'];

const TEST_TYPE_TABS = [
  { id: 'FULLSCALE', label: 'Fullscale', icon: <LibraryBooksIcon sx={{ fontSize: 13 }} />, color: '#6366f1' },
  { id: 'TOPIC', label: 'Topic', icon: <TopicIcon sx={{ fontSize: 13 }} />, color: '#3b82f6' },
  { id: 'SUBTOPIC', label: 'Subtopic', icon: <AccountTreeIcon sx={{ fontSize: 13 }} />, color: '#10b981' },
  { id: 'MIXED', label: 'Mixed', icon: <LayersIcon sx={{ fontSize: 13 }} />, color: '#f59e0b' },
];

const WIZARD_TEST_TYPES = [
  { id: 'fullscale', label: 'Full Scale', badge: 'All topics & subtopics', icon: <LibraryBooksIcon sx={{ fontSize: 18 }} />, color: '#6366f1', selNote: 'Auto-includes all topics & subtopics' },
  { id: 'topic', label: 'Topic Level', badge: '1 topic, all subtopics', icon: <TopicIcon sx={{ fontSize: 18 }} />, color: '#3b82f6', selNote: 'Pick 1 topic → all its subtopics included' },
  { id: 'subtopic', label: 'Subtopic Level', badge: '1 topic + 1 subtopic', icon: <AccountTreeIcon sx={{ fontSize: 18 }} />, color: '#10b981', selNote: 'Pick 1 topic → pick 1 subtopic' },
  { id: 'custom', label: 'Multi-Select', badge: 'Mix freely', icon: <LayersIcon sx={{ fontSize: 18 }} />, color: '#f59e0b', selNote: 'Pick multiple topics & subtopics' },
];

const LEVELS = {
  easy: { label: 'Easy', color: '#10b981', emoji: '🟢' },
  medium: { label: 'Medium', color: '#f59e0b', emoji: '🟡' },
  hard: { label: 'Hard', color: '#ef4444', emoji: '🔴' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const extractData = (res: any): PreloadedTest[] => {
  if (res?.data && Array.isArray(res.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
};

const getDiffLabel = (dd: PreloadedTest['difficulty_distribution']) => {
  if (dd?.Easy) return { label: 'Easy', color: '#10b981' };
  if (dd?.Medium) return { label: 'Medium', color: '#f59e0b' };
  if (dd?.Hard || (dd as any)?.hard) return { label: 'Hard', color: '#ef4444' };
  return { label: 'Mixed', color: '#8b5cf6' };
};

const QUESTION_COUNTS = [
  { id: '5', value: 5 },
  { id: '10', value: 10 },
  { id: '20', value: 20 },
  { id: '30', value: 30 },
];

const normalizeClassValue = (value: string) => {
  if (!value) return '';
  return value
    .split('-')
    .map((part) => String(Number(part.trim())))
    .join('-');
};

const buildSubjectHierarchy = (items: any[]) => {
  const hierarchy: Record<string, Record<string, string[]>> = {};
  items.forEach((subjectData: any) => {
    if (!subjectData?.subject) return;
    if (!hierarchy[subjectData.subject]) hierarchy[subjectData.subject] = {};
    (subjectData.topics || []).forEach((topic: any) => {
      hierarchy[subjectData.subject][topic.topic] = topic.subtopics?.map((s: any) => s.subtopic) ?? [];
    });
  });
  return hierarchy;
};

// ─── ChipPicker ───────────────────────────────────────────────────────────────
const ChipPicker: React.FC<{ items: string[]; selected: string[]; onToggle: (v: string) => void; colorOff?: number }> = ({ items, selected, onToggle, colorOff = 0 }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
    {items.map((item, idx) => {
      const on = selected.includes(item);
      const c = CHIP_COLORS[(idx + colorOff) % CHIP_COLORS.length];
      return (
        <Chip key={item} label={item} onClick={() => onToggle(item)}
          icon={on ? <CheckCircleIcon sx={{ fontSize: '12px !important', color: '#fff !important' }} /> : undefined}
          sx={{
            cursor: 'pointer', fontSize: { xs: 11, sm: 12 }, height: 28, fontWeight: on ? 700 : 500,
            bgcolor: on ? c : `${c}14`, color: on ? '#fff' : c,
            border: `1.5px solid ${c}${on ? '' : '45'}`, transition: 'all 0.15s ease',
            '&:hover': { bgcolor: on ? c : `${c}28`, transform: 'translateY(-1px)' }
          }} />
      );
    })}
  </Box>
);

// ─── Subject Card ─────────────────────────────────────────────────────────────
const SubjectCard: React.FC<{ subject: string; selected: boolean; onClick: () => void }> = ({ subject, selected, onClick }) => {
  const cfg = getSCfg(subject);
  return (
    <Card onClick={onClick} elevation={selected ? 4 : 0}
      sx={{
        cursor: 'pointer', border: `2px solid ${selected ? cfg.color : '#e5e7eb'}`, borderRadius: 2,
        bgcolor: selected ? `${cfg.color}10` : '#fff', transition: 'all 0.18s ease', position: 'relative', overflow: 'visible',
        '&:hover': { border: `2px solid ${cfg.color}`, transform: 'translateY(-2px)', boxShadow: `0 6px 18px ${cfg.color}25` }
      }}>
      {selected && <CheckCircleIcon sx={{ position: 'absolute', top: -7, right: -7, fontSize: 18, color: cfg.color, bgcolor: '#fff', borderRadius: '50%', zIndex: 1 }} />}
      <CardContent sx={{ p: '8px 4px !important', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, textAlign: 'center' }}>
        <Box sx={{
          width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, borderRadius: '50%',
          background: selected ? cfg.gradient : `${cfg.color}16`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: { xs: 18, sm: 22 },
          boxShadow: selected ? `0 3px 10px ${cfg.color}40` : 'none', transition: 'all 0.18s ease'
        }}>
          {cfg.icon}
        </Box>
        <Typography fontWeight={selected ? 700 : 600} fontSize={{ xs: 9, sm: 11 }} color={selected ? cfg.color : '#374151'} sx={{ lineHeight: 1.2 }}>
          {subject}
        </Typography>
      </CardContent>
    </Card>
  );
};

const TestDetailPanel: React.FC<{ test: PreloadedTest; onBack: () => void }> = ({ test, onBack }) => {
  const cfg = getSCfg(test.subject);
  const diff = getDiffLabel(test.difficulty_distribution);
  const totalDiff = Object.values(test.difficulty_distribution).reduce((a, b) => (a || 0) + (b || 0), 0) || 1;

  const TOPIC_LIMIT = 4;
  const SUBTOPIC_LIMIT = 5;
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [showAllSubtopics, setShowAllSubtopics] = useState(false);

  const visibleTopics = showAllTopics
    ? (test.topics_covered || [])
    : (test.topics_covered || []).slice(0, TOPIC_LIMIT);

  const visibleSubtopics = showAllSubtopics
    ? (test.subtopics_covered || [])
    : (test.subtopics_covered || []).slice(0, SUBTOPIC_LIMIT);

  const hiddenTopics = (test.topics_covered?.length || 0) - TOPIC_LIMIT;
  const hiddenSubtopics = (test.subtopics_covered?.length || 0) - SUBTOPIC_LIMIT;

  return (
    <Fade in timeout={300}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Back + header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexShrink: 0 }}>
          <Button size="small" startIcon={<ArrowBackIcon sx={{ fontSize: 13 }} />}
            onClick={onBack}
            sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, px: 1, py: 0.4, borderRadius: 1.5, color: '#6b7280', minWidth: 0 }}>
            Back
          </Button>
          <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: cfg.color }} />
          <Typography fontWeight={800} fontSize={14} color="#111827">Test Details</Typography>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: 2 } }}>

          {/* Hero card */}
          <Paper elevation={0} sx={{ p: 1.25, borderRadius: 2, background: `linear-gradient(135deg, ${cfg.color}12, ${cfg.color}06)`, border: `1.5px solid ${cfg.color}25`, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: cfg.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                {cfg.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.4 }}>
                  <Typography fontWeight={800} fontSize={13} color="#111827" lineHeight={1.3}>
                    {test.subject} — {test.test_type}
                  </Typography>
                  <Typography fontSize={10} fontFamily="monospace" color="#111827" sx={{
                    bgcolor: '#f1f5f9', border: '1px solid #e2e8f0',
                    borderRadius: 0.75, px: 0.75, py: 0.15, lineHeight: 1.5, flexShrink: 0
                  }}>
                    {test.test_id}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                  {[
                    { icon: <AssignmentIcon sx={{ fontSize: 11 }} />, text: `${test.total_questions} Questions` },
                    { icon: <TimerIcon sx={{ fontSize: 11 }} />, text: `${test.test_time} min` },
                    ...(test.maximum_marks ? [{ icon: <StarIcon sx={{ fontSize: 11 }} />, text: `${test.maximum_marks} Marks` }] : []),
                  ].map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#6b7280' }}>
                      {s.icon}
                      <Typography fontSize={10}>{s.text}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Difficulty distribution */}
          <Box sx={{ mb: 1.5 }}>
            <Typography fontWeight={700} fontSize={11} color="#374151" mb={0.5}>Difficulty Distribution</Typography>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {Object.entries(test.difficulty_distribution).map(([key, val]) => {
                if (!val) return null;
                const dc = key.toLowerCase() === 'easy' ? '#10b981' : key.toLowerCase() === 'medium' ? '#f59e0b' : '#ef4444';
                const bg = key.toLowerCase() === 'easy' ? '#f0fdf4' : key.toLowerCase() === 'medium' ? '#fffbeb' : '#fef2f2';
                const br = key.toLowerCase() === 'easy' ? '#d1fae5' : key.toLowerCase() === 'medium' ? '#fde68a' : '#fecaca';
                const pct = Math.round(((val as number) / totalDiff) * 100);
                return (
                  <Box key={key} sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 0.5, py: 0.4, px: 0.5, borderRadius: 1,
                    bgcolor: bg, border: `1px solid ${br}`,
                  }}>
                    <Typography fontWeight={700} fontSize={12} color={dc} lineHeight={1}>{val}</Typography>
                    <Typography fontSize={9} color={dc} lineHeight={1}>{key} ({pct}%)</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Topics covered */}
          {(test.topics_covered?.length || 0) > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography fontWeight={700} fontSize={11} color="#374151" mb={0.6}>
                📌 Topics Covered ({test.topics_covered.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {visibleTopics.map((t, i) => (
                  <Chip key={t} label={t} size="small"
                    sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: `${CHIP_COLORS[i % CHIP_COLORS.length]}12`, color: CHIP_COLORS[i % CHIP_COLORS.length], border: `1px solid ${CHIP_COLORS[i % CHIP_COLORS.length]}30` }} />
                ))}
                {!showAllTopics && hiddenTopics > 0 && (
                  <Chip label={`+${hiddenTopics} more`} size="small" onClick={() => setShowAllTopics(true)}
                    sx={{ height: 22, fontSize: 10, fontWeight: 600, cursor: 'pointer', bgcolor: '#f1f5f9', color: '#6b7280', border: '1px solid #e5e7eb', '&:hover': { bgcolor: '#e5e7eb' } }} />
                )}
                {showAllTopics && (
                  <Chip label="View less" size="small" onClick={() => setShowAllTopics(false)}
                    sx={{ height: 22, fontSize: 10, fontWeight: 600, cursor: 'pointer', bgcolor: '#f1f5f9', color: '#6b7280', border: '1px solid #e5e7eb', '&:hover': { bgcolor: '#e5e7eb' } }} />
                )}
              </Box>
            </Box>
          )}

          {/* Subtopics covered */}
          {(test.subtopics_covered?.length || 0) > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography fontWeight={700} fontSize={11} color="#374151" mb={0.6}>
                🎯 Subtopics Covered ({test.subtopics_covered.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {visibleSubtopics.map((st) => (
                  <Chip key={st} label={st} size="small"
                    sx={{ height: 22, fontSize: 10, fontWeight: 500, bgcolor: '#f8fafc', color: '#6b7280', border: '1px solid #e5e7eb' }} />
                ))}
                {!showAllSubtopics && hiddenSubtopics > 0 && (
                  <Chip label={`+${hiddenSubtopics} more`} size="small" onClick={() => setShowAllSubtopics(true)}
                    sx={{ height: 22, fontSize: 10, fontWeight: 600, cursor: 'pointer', bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', '&:hover': { bgcolor: '#dbeafe' } }} />
                )}
                {showAllSubtopics && (
                  <Chip label="View less" size="small" onClick={() => setShowAllSubtopics(false)}
                    sx={{ height: 22, fontSize: 10, fontWeight: 600, cursor: 'pointer', bgcolor: '#f1f5f9', color: '#6b7280', border: '1px solid #e5e7eb', '&:hover': { bgcolor: '#e5e7eb' } }} />
                )}
              </Box>
            </Box>
          )}

          {/* Start / Submitted */}
          {/* {test.test_submitted ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.6, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1.5px solid #d1fae5' }}>
              <CheckCircleIcon sx={{ fontSize: 14, color: '#059669' }} />
              <Typography fontWeight={700} fontSize={12} color="#059669">Test already submitted</Typography>
            </Box>
          ) */}
          {test.test_submitted ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.6, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1.5px solid #d1fae5' }}>
                <CheckCircleIcon sx={{ fontSize: 14, color: '#059669' }} />
                <Typography fontWeight={700} fontSize={12} color="#059669">Test already submitted</Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<AssignmentIcon sx={{ fontSize: 13 }} />}
                onClick={() => window.open(`/r/test/result/${test.test_id}`, '_blank')}
                sx={{
                  textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.5, py: 0.6, borderRadius: 1.5,
                  bgcolor: '#059669', '&:hover': { bgcolor: '#047857' },
                  boxShadow: '0 2px 8px rgba(5,150,105,0.35)'
                }}
              >
                View Result
              </Button>
            </Box>
          ) : (
            <Button fullWidth variant="contained" size="large"
              startIcon={<PlayArrowIcon />}
              onClick={() => window.open(`/r/test/${test.test_id}`, '_blank')}
              sx={{
                textTransform: 'none', fontWeight: 800, fontSize: 14, py: 1.25, borderRadius: 2,
                background: cfg.gradient, boxShadow: `0 4px 14px ${cfg.color}40`,
                '&:hover': { background: cfg.gradient, filter: 'brightness(0.93)' }
              }}>
              Start Test
            </Button>
          )}

        </Box>
      </Box>
    </Fade>
  );
};

// ─── Fullscale Sidebar Row ────────────────────────────────────────────────────
const FullscaleRow: React.FC<{ test: PreloadedTest; active: boolean; onClick: () => void; submitted?: boolean }> = ({ test, active, onClick, submitted }) => {
  const cfg = getSCfg(test.subject);
  const diff = getDiffLabel(test.difficulty_distribution);
  const tooltipContent = (
    <Box sx={{ p: 0.5, maxWidth: 240 }}>
      <Typography fontSize={11} fontWeight={700} mb={0.5}>📌 Topics</Typography>
      {test.topics_covered?.map(t => <Typography key={t} fontSize={10}>• {t}</Typography>)}
      {test.subtopics_covered && test.subtopics_covered.length > 0 && (
        <>
          <Typography fontSize={11} fontWeight={700} mt={0.75} mb={0.5}>🎯 Subtopics</Typography>
          {test.subtopics_covered.slice(0, 6).map(s => <Typography key={s} fontSize={10}>• {s}</Typography>)}
          {test.subtopics_covered.length > 6 && <Typography fontSize={10} color="#9ca3af">+{test.subtopics_covered.length - 6} more</Typography>}
        </>
      )}
      <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
        {Object.entries(test.difficulty_distribution).filter(([, v]) => v).map(([k, v]) => {
          const dc = k.toLowerCase() === 'easy' ? '#10b981' : k.toLowerCase() === 'medium' ? '#f59e0b' : '#ef4444';
          return <Typography key={k} fontSize={10} fontWeight={700} sx={{ color: dc }}>{k}: {v}</Typography>;
        })}
      </Box>
    </Box>
  );

  return (
    <Box onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.7, mb: 0.4, borderRadius: 1.5, cursor: 'pointer',
        bgcolor: active ? `${cfg.color}12` : submitted ? '#f0fdf4' : '#f8fafc',
        border: `1.5px solid ${active ? cfg.color + '60' : submitted ? '#d1fae5' : '#e5e7eb'}`,
        '&:hover': { bgcolor: `${cfg.color}08`, borderColor: `${cfg.color}40` }, transition: 'all 0.12s ease'
      }}>
      {active && <FiberManualRecordIcon sx={{ fontSize: 7, color: cfg.color, flexShrink: 0 }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Typography fontSize={11} fontWeight={active ? 700 : 600} color={active ? cfg.color : '#374151'}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {test.test_id}
          </Typography>
          <Tooltip title={tooltipContent} arrow placement="right">
            <InfoOutlinedIcon sx={{ fontSize: 13, color: active ? cfg.color : '#9ca3af', flexShrink: 0, '&:hover': { color: cfg.color } }} />
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.2 }}>
          <Typography fontSize={9} color="#9ca3af">{test.total_questions}Q · {test.test_time}m</Typography>
          {submitted && <Chip label="Done" size="small" sx={{ height: 13, fontSize: 8, fontWeight: 700, bgcolor: '#d1fae5', color: '#059669', '& .MuiChip-label': { px: 0.5 } }} />}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar: React.FC<{
  selectedSubject: string;
  selectedClass: string;
  actions: any;
  theme: any;
  userId: string;
  mobile: string;
  activeConversationId: string | null;
  activeTestId: string | null;
  onTestClick: (t: PreloadedTest) => void;
  onConvClick: (id: string) => void;
}> = ({ selectedSubject, selectedClass, actions, theme, userId, mobile, activeConversationId, activeTestId, onTestClick, onConvClick }) => {
  const [typeTabIdx, setTypeTabIdx] = useState(0);
  const [statusTabIdx, setStatusTabIdx] = useState(0);

  const [availableTests, setAvailableTests] = useState<PreloadedTest[]>([]);
  const [submittedTests, setSubmittedTests] = useState<PreloadedTest[]>([]);
  const [customModeTests, setCustomModeTests] = useState<PreloadedTest[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const currentTab = TEST_TYPE_TABS[typeTabIdx] || TEST_TYPE_TABS[0];

  useEffect(() => {
    setTypeTabIdx(0); setStatusTabIdx(0);
    setAvailableTests([]); setSubmittedTests([]); setCustomModeTests([]); setConversations([]);
  }, [selectedSubject]);

  useEffect(() => {
    if (!selectedSubject || !selectedClass) return;
    fetchAll();
  }, [selectedSubject, selectedClass, typeTabIdx]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2,] = await Promise.allSettled([
        actions.getTestPaper({
          class: selectedClass, subject: selectedSubject,
          test_mode: 'custom',
          test_type: currentTab.id,
          check_submitted: 'no',
        } as any),
        actions.getConversationList({
          user_id: userId,
          phone_number: mobile ? `91${mobile}` : '',
          flow_type: 'test',
          parameters: { subject: selectedSubject },
        }),
      ]);

      setAvailableTests(r1.status === 'fulfilled' ? extractData(r1.value) : []);
      if (r2.status === 'fulfilled' && Array.isArray(r2.value?.data)) {
        setConversations(r2.value.data.map((c: any) => ({
          ...c, last_query: c.last_query || c.query || 'Untitled',
        })));
      }
    } finally { setLoading(false); }
  }, [selectedSubject, selectedClass, currentTab.id]);

  const displayedTests = statusTabIdx === 0 ? availableTests : submittedTests;

  useEffect(() => {
    if (statusTabIdx !== 1) return;
    if (!selectedSubject || !selectedClass) return;
    fetchSubmitted();
  }, [statusTabIdx, selectedSubject, selectedClass, currentTab.id]);

  const fetchSubmitted = useCallback(async () => {
    setLoading(true);
    try {
      const res = await actions.getTestPaper({
        class: selectedClass,
        test_type: currentTab.id,
        check_submitted: 'yes',
        subject: selectedSubject,
      });
      setSubmittedTests(extractData(res));
    } catch {
      setSubmittedTests([]);
    } finally { setLoading(false); }
  }, [selectedSubject, selectedClass, currentTab.id]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 1.25, background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`, color: '#fff', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <LibraryBooksIcon sx={{ fontSize: 15 }} />
          <Typography fontWeight={800} fontSize={13}>Test Library</Typography>
          {/* Show resolved class in sidebar header */}
          {selectedClass && (
            <Typography sx={{ fontSize: 10, color: '#fff', px: 0.75, py: 0.15, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)' }}>
              Class {selectedClass}
            </Typography>
          )}
        </Box>
        <Typography fontSize={10} sx={{ opacity: 0.78, mt: 0.2 }}>
          {selectedSubject || 'Select a subject first'}
        </Typography>
      </Box>

      {/* {!selectedSubject ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2, gap: 1 }}>
          <Box sx={{ fontSize: 28 }}>📋</Box>
          <Typography fontSize={12} fontWeight={600} color="#374151" textAlign="center">No subject selected</Typography>
          <Typography fontSize={11} color="#9ca3af" textAlign="center">Pick a subject above</Typography>
        </Box>
      ) : ( */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* 4 Test Type tabs */}
          <Box sx={{ borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            <Tabs value={typeTabIdx} onChange={(_, v) => { setTypeTabIdx(v); setStatusTabIdx(0); }}
              variant="scrollable" scrollButtons="auto"
              sx={{ minHeight: 32, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 10, minHeight: 32, py: 0.25, px: 1, minWidth: 0 }, '& .MuiTabs-indicator': { height: 2.5, borderRadius: 2 } }}>
              {TEST_TYPE_TABS.map((t, i) => (
                <Tab key={t.id} label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                    <Box sx={{ color: typeTabIdx === i ? t.color : 'inherit', display: 'flex' }}>{t.icon}</Box>
                    {t.label}
                  </Box>
                } sx={{ '&.Mui-selected': { color: t.color } }} />
              ))}
            </Tabs>
          </Box>

          {/* Available / Submitted sub-tabs */}
          <Box sx={{ borderBottom: '1px solid #f1f5f9', flexShrink: 0, bgcolor: '#fafafa' }}>
            <Tabs value={statusTabIdx} onChange={(_, v) => setStatusTabIdx(v)} variant="fullWidth"
              sx={{ minHeight: 28, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 10, minHeight: 28, py: 0 }, '& .MuiTabs-indicator': { height: 2, borderRadius: 2 } }}>
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  Available
                  {availableTests.length > 0 && (
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: currentTab.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800 }}>
                      {availableTests.length}
                    </Box>
                  )}
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  Submitted
                  {submittedTests.length > 0 && (
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800 }}>
                      {submittedTests.length}
                    </Box>
                  )}
                </Box>
              } />
            </Tabs>
          </Box>

          {loading && <LinearProgress sx={{ height: 2 }} />}

          {/* Scrollable content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1, '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: 2 } }}>
            {currentTab.id === 'FULLSCALE' && (
              <>
                {displayedTests.length === 0 && !loading && (
                  <EmptyState type={statusTabIdx === 0 ? 'available' : 'submitted'} />
                )}
                {displayedTests.map(test => (
                  <FullscaleRow key={test._id} test={test}
                    active={activeTestId === test._id}
                    submitted={statusTabIdx === 1}
                    onClick={() => onTestClick(test)} />
                ))}
              </>
            )}

            {currentTab.id === 'TOPIC' && (
              <>
                {displayedTests.length === 0 && !loading && (
                  <EmptyState type={statusTabIdx === 0 ? 'available' : 'submitted'} />
                )}
                {displayedTests.map(test => (
                  <FullscaleRow key={test._id} test={test}
                    active={activeTestId === test._id}
                    submitted={statusTabIdx === 1}
                    onClick={() => onTestClick(test)} />
                ))}
              </>
            )}

            {currentTab.id === 'SUBTOPIC' && (
              <>
                {displayedTests.length === 0 && !loading && (
                  <EmptyState type={statusTabIdx === 0 ? 'available' : 'submitted'} />
                )}
                {displayedTests.map(test => (
                  <FullscaleRow key={test._id} test={test}
                    active={activeTestId === test._id}
                    submitted={statusTabIdx === 1}
                    onClick={() => onTestClick(test)} />
                ))}
              </>
            )}

            {currentTab.id === 'MIXED' && (
              <>
                {displayedTests.length === 0 && !loading ? (
                  <EmptyState type={statusTabIdx === 0 ? 'available' : 'submitted'} />
                ) : (
                  displayedTests.map(test => (
                    <Box key={test._id} sx={{ mb: 0.75 }}>
                      <MiniTestCard test={test} active={activeTestId === test._id}
                        submitted={statusTabIdx === 1} onClick={() => onTestClick(test)} />
                    </Box>
                  ))
                )}
              </>
            )}
          </Box>
        </Box>
      {/* )} */}
    </Box>
  );
};

// ─── Mini Test Card ───────────────────────────────────────────────────────────
const MiniTestCard: React.FC<{ test: PreloadedTest; active: boolean; onClick: () => void; submitted?: boolean }> = ({ test, active, onClick, submitted = false }) => {
  const cfg = getSCfg(test.subject);
  const diff = getDiffLabel(test.difficulty_distribution);
  const label = test.topics_covered?.slice(0, 2).join(' & ') || test.subject;

  return (
    <Box onClick={onClick}
      sx={{
        p: '8px 10px', borderRadius: 1.75, cursor: 'pointer',
        bgcolor: active ? `${cfg.color}10` : submitted ? '#f0fdf4' : '#f8fafc',
        border: `1.5px solid ${active ? cfg.color + '60' : submitted ? '#d1fae5' : '#e5e7eb'}`,
        '&:hover': { borderColor: cfg.color, bgcolor: `${cfg.color}06` }, transition: 'all 0.12s'
      }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: 1, background: cfg.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
          {cfg.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize={11} fontWeight={active ? 700 : 600} color={active ? cfg.color : '#111827'}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3, mb: 0.25 }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography fontSize={9} color="#9ca3af">{test.total_questions}Q · {test.test_time}m</Typography>
            <Chip label={diff.label} size="small" sx={{ height: 13, fontSize: 8, fontWeight: 700, bgcolor: `${diff.color}14`, color: diff.color, '& .MuiChip-label': { px: 0.5 } }} />
            {submitted && <Chip label="Done" size="small" sx={{ height: 13, fontSize: 8, bgcolor: '#d1fae5', color: '#059669', '& .MuiChip-label': { px: 0.5 } }} />}
            {active && <FiberManualRecordIcon sx={{ fontSize: 7, color: cfg.color }} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Empty state ──────────────────────────────────────────────────────────────
const EmptyState: React.FC<{ type: 'available' | 'submitted' }> = ({ type }) => (
  <Box sx={{ p: 2, textAlign: 'center' }}>
    <Typography fontSize={22} mb={0.5}>{type === 'available' ? '📝' : '✅'}</Typography>
    <Typography fontSize={11} fontWeight={600} color="#374151">{type === 'available' ? 'No available tests' : 'No submitted tests'}</Typography>
    <Typography fontSize={10} color="#9ca3af" mt={0.25}>
      {type === 'available' ? 'Try another type or create custom' : 'Complete a test to see it here'}
    </Typography>
  </Box>
);

// ─── Wizard Test Type Card ────────────────────────────────────────────────────
const TestTypeCard: React.FC<{ type: typeof WIZARD_TEST_TYPES[0]; selected: boolean; onClick: () => void }> = ({ type, selected, onClick }) => (
  <Card onClick={onClick} elevation={selected ? 2 : 0}
    sx={{
      cursor: 'pointer', border: `2px solid ${selected ? type.color : '#e5e7eb'}`, borderRadius: 1.5,
      bgcolor: selected ? `${type.color}10` : '#fafafa', transition: 'all 0.18s ease',
      '&:hover': { border: `2px solid ${type.color}`, transform: 'translateY(-1px)', boxShadow: `0 4px 14px ${type.color}20` }
    }}>
    <CardContent sx={{ p: '10px 12px !important' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 1, bgcolor: selected ? type.color : `${type.color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: selected ? '#fff' : type.color, flexShrink: 0
        }}>
          {type.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={700} fontSize={12} color={selected ? type.color : '#1f2937'}>{type.label}</Typography>
          <Typography fontSize={10} color="text.secondary">{type.badge}</Typography>
        </Box>
        {selected && <CheckCircleIcon sx={{ fontSize: 15, color: type.color, flexShrink: 0 }} />}
      </Box>
      {selected && (
        <Box sx={{ mt: 0.75, px: 0.5, py: 0.4, borderRadius: 1, bgcolor: `${type.color}12`, border: `1px dashed ${type.color}45` }}>
          <Typography fontSize={10} fontWeight={600} color={type.color}>💡 {type.selNote}</Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const TestCreatorPage = () => {
  const actions = AiActions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const loggedInUser = useRecoilValue(userInformationAtom);
  const userId = loggedInUser?.id || loggedInUser?.user_id || '';
  const mobile = loggedInUser?.mobile || '';
  const loggedInUsername = loggedInUser?.username || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const subjectFromUrl = searchParams.get('subject');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [selectedSubtopics, setSelectedSubtopics] = useState<string[]>([]);
  const [testLevel, setTestLevel] = useState('easy');
  const [testType, setTestType] = useState('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [instructionSet, setInstructionSet] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeTest, setActiveTest] = useState<PreloadedTest | null>(null);
  const { state } = useWebSocketAiContext();
  const [numQuestions, setNumQuestions] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState('30');
  const isSuperuser = loggedInUser?.is_superuser === true || loggedInUser?.profile?.name === 'Admin';
  // ---------------------------------------------------------------------------
  // FIX: derive selectedClass dynamically from entityData (same as
  // DoubtAssistantPage) instead of hardcoding '9'
  // ---------------------------------------------------------------------------
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [superuserSelectedClass, setSuperuserSelectedClass] = useState('');

  const [viewMode, setViewMode] = useState<ViewMode>('browse');
  const [step, setStep] = useState<Step>('testType');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [browseTests, setBrowseTests] = useState<PreloadedTest[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);

  const { loading: subjectsLoading, subjects, subjectHierarchy, knowledgeMapData, entityData: data } = useKnowledgeMapSubjects(true);
  const effectiveClass = isSuperuser ? superuserSelectedClass : selectedClass;

  useEffect(() => {
    if (isSuperuser) return;
    if (!subjectFromUrl) return;
    if (selectedSubject === subjectFromUrl) return;
    handleSubjectSelect(subjectFromUrl);
  }, [subjectFromUrl, isSuperuser]);

  const superuserFilteredKnowledgeMap = useMemo(() => {
    if (!Array.isArray(knowledgeMapData) || !knowledgeMapData.length) return [];
    const orgFilteredData = selectedOrganisation?.organisation_id
      ? knowledgeMapData.filter((item: any) => item.organisation_id === selectedOrganisation.organisation_id)
      : knowledgeMapData;

    const boardFilteredData = selectedBoard
      ? orgFilteredData.filter((item: any) => item.board === selectedBoard)
      : orgFilteredData;

    if (!isSuperuser || !superuserSelectedClass) return boardFilteredData;

    const normalizedSelectedClass = normalizeClassValue(superuserSelectedClass);
    return boardFilteredData.filter((item: any) => {
      if (!item.class) return false;
      const itemNormalized = normalizeClassValue(String(item.class).trim());
      if (itemNormalized === normalizedSelectedClass) return true;
      if (itemNormalized.includes('-')) {
        const [start, end] = itemNormalized.split('-').map(Number);
        const selectedNum = Number(superuserSelectedClass);
        return !Number.isNaN(selectedNum) && selectedNum >= start && selectedNum <= end;
      }
      return false;
    });
  }, [knowledgeMapData, selectedOrganisation, selectedBoard, superuserSelectedClass, isSuperuser]);

  const boardOptions = useMemo(() => {
    const source = selectedOrganisation?.organisation_id
      ? knowledgeMapData.filter((item: any) => item.organisation_id === selectedOrganisation.organisation_id)
      : knowledgeMapData;
    return [...new Set(source.map((item: any) => item.board).filter(Boolean))].sort().map((board) => ({ label: board, value: board }));
  }, [knowledgeMapData, selectedOrganisation]);

  const classOptions = useMemo(() => {
    const source = selectedOrganisation?.organisation_id
      ? knowledgeMapData.filter((item: any) => item.organisation_id === selectedOrganisation.organisation_id)
      : knowledgeMapData;
    const boardFiltered = selectedBoard ? source.filter((item: any) => item.board === selectedBoard) : source;
    const classSet = new Set<string>();
    boardFiltered.forEach((item: any) => {
      if (!item.class) return;
      classSet.add(String(item.class).trim());
    });
    return Array.from(classSet).sort((a, b) => {
      const aIsRange = a.includes('-');
      const bIsRange = b.includes('-');
      if (!aIsRange && !bIsRange) return Number(a) - Number(b);
      if (!aIsRange && bIsRange) return -1;
      if (aIsRange && !bIsRange) return 1;
      return a.localeCompare(b);
    }).map((cls) => ({ label: `Class ${cls}`, value: cls }));
  }, [knowledgeMapData, selectedOrganisation, selectedBoard]);

  const availableSuperuserSubjects = useMemo(() => {
    return [...new Set(superuserFilteredKnowledgeMap.map((item: any) => item.subject).filter(Boolean))].sort();
  }, [superuserFilteredKnowledgeMap]);

  const activeSubjectHierarchy = isSuperuser ? buildSubjectHierarchy(superuserFilteredKnowledgeMap) : subjectHierarchy;
  const activeSubjects = isSuperuser
    ? availableSuperuserSubjects.map((subject) => ({ label: subject, value: subject }))
    : subjects;

  // Derive class from entityData by matching loggedInUsername — mirrors
  // DoubtAssistantPage's handleClassGroupFromData
  useEffect(() => {
    if (isSuperuser || !data?.items?.length || !loggedInUsername) return;
    data.items.forEach((item: any) => {
      if (item?.entity?.username?.toLowerCase() === loggedInUsername.toLowerCase()) {
        setSelectedClass(item.entity.class ?? '');
      }
    });
  }, [data, loggedInUsername]);

  const getInstruction = (s: string) => {
    if (!knowledgeMapData || !Array.isArray(knowledgeMapData)) return '';
    return knowledgeMapData.find((i: any) => i.subject === s)?.test_instruction_set || '';
  };

  const availableTopics = selectedSubject ? Object.keys(activeSubjectHierarchy[selectedSubject] || {}) : [];
  const availableSubtopics = (() => {
    if (!selectedSubject || selectedTopics.length === 0) return [];
    const set = new Set<string>();
    selectedTopics.forEach(t => (activeSubjectHierarchy[selectedSubject]?.[t] || []).forEach((s: string) => set.add(s)));
    return Array.from(set);
  })();

  const resetWizardState = useCallback(() => {
    setSelectedTopics([]);
    setSelectedSubtopics([]);
    setTestType('');
    setTestResult(null);
    setError('');
    setViewMode('browse');
    setStep('testType');
    setActiveTest(null);
    setInstructionSet('');
  }, []);

  const handleOrganisationChange = (org: any) => {
    setSelectedOrganisation(org);
    setSelectedBoard('');
    setSuperuserSelectedClass('');
    setSelectedSubject('');
    resetWizardState();
    setBrowseTests([]);
  };

  const handleBoardSelect = (board: string) => {
    setSelectedBoard(board);
    setSuperuserSelectedClass('');
    setSelectedSubject('');
    resetWizardState();
    setBrowseTests([]);
  };

  const handleClassSelect = (cls: string) => {
    setSuperuserSelectedClass(cls);
    setSelectedSubject('');
    resetWizardState();
    setBrowseTests([]);
  };

  const handleSubjectSelect = (subject: string) => {
    if (isSuperuser && !effectiveClass) return;
    setSelectedSubject(subject); setSelectedTopics([]); setSelectedSubtopics([]);
    setTestType(''); setTestResult(null); setError('');
    setViewMode('browse'); setStep('testType'); setActiveTest(null);
    setInstructionSet(getInstruction(subject));
    setBrowseTests([]);
    if (!isSuperuser) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('subject', subject);
        return next;
      });
    }
    setBrowseLoading(true);
    actions.getTestPaper({ class: effectiveClass, subject, test_started: false, check_submitted: 'no' })
      .then((res: any) => setBrowseTests(extractData(res)))
      .catch(() => { })
      .finally(() => setBrowseLoading(false));
  };

  const handleTopicToggle = (topic: string) => {
    if (testType === 'fullscale') return;
    if (testType === 'topic' || testType === 'subtopic') {
      setSelectedTopics(p => p.includes(topic) ? [] : [topic]); setSelectedSubtopics([]);
    } else {
      setSelectedTopics(prev => {
        const next = prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic];
        setSelectedSubtopics(s => s.filter(sub => next.some(t => activeSubjectHierarchy[selectedSubject]?.[t]?.includes(sub))));
        return next;
      });
    }
  };

  const handleSubtopicToggle = (sub: string) => {
    if (testType === 'subtopic') setSelectedSubtopics(p => p.includes(sub) ? [] : [sub]);
    else setSelectedSubtopics(p => p.includes(sub) ? p.filter(s => s !== sub) : [...p, sub]);
  };

  const handleTestTypeSelect = (type: string) => {
    setTestType(type); setSelectedTopics([]); setSelectedSubtopics([]); setTestResult(null);
    setStep(type === 'fullscale' ? 'level' : 'topic');
  };

  const handleTestClick = (test: PreloadedTest) => {
    setActiveTest(test);
    setActiveConversationId(null);
    setViewMode('testDetail');
    if (isMobile) setSidebarOpen(false);
  };

  const handleConvClick = async (convId: string) => {
    setActiveConversationId(convId);
    setActiveTest(null);
    if (isMobile) setSidebarOpen(false);
    try {
      const res = await actions.getChatHistory({ conversation_id: convId, ai_model: 'mdai' });
      if (!res?.status || !Array.isArray(res.data)) return;
      const lastRes = res.data[res.data.length - 1]?.response?.response;
      if (lastRes) {
        setTestResult({ testUrl: lastRes.test_url || '', responseText: lastRes.response || '', conversation_id: convId });
        setConversationId(convId);
        setViewMode('browse');
      }
    } catch { }
  };

  const handleCreateTest = async () => {
    setLoading(true);
    setError('');
    setTestResult(null);

    try {
      const topicsToSend = testType === 'fullscale' ? [] : selectedTopics;
      const subtopicsToSend = (testType === 'fullscale' || testType === 'topic') ? [] : selectedSubtopics;
      const testTypeUpper = testType === 'custom' ? 'MIXED' : testType.toUpperCase();

      const topic_sub_topic: Record<string, string[]> = {};
      if (testType === 'fullscale') {
        availableTopics.forEach(topic => {
          topic_sub_topic[topic] = subjectHierarchy[selectedSubject]?.[topic] || [];
        });
      } else if (testType === 'topic') {
        topicsToSend.forEach(topic => {
          topic_sub_topic[topic] = subjectHierarchy[selectedSubject]?.[topic] || [];
        });
      } else {
        topicsToSend.forEach(topic => {
          const allSubs = subjectHierarchy[selectedSubject]?.[topic] || [];
          const filtered = subtopicsToSend.filter(sub => allSubs.includes(sub));
          topic_sub_topic[topic] = filtered.length > 0 ? filtered : allSubs;
        });
      }

      const res = await actions.getPracticeTest({
        subject: selectedSubject,
        class: effectiveClass,
        board: selectedBoard || 'CBSE',
        topic_sub_topic,
        test_type: testTypeUpper,
        test_mode: 'custom',
        num_questions: numQuestions ? parseInt(numQuestions) : undefined,
        test_time: selectedTime ? parseInt(selectedTime) : undefined,
      });

      const resData = res?.data || res;
      const testUrl = resData?.test_details?.test_url || '';
      const responseText = resData?.response || resData?.response?.response || resData?.message || '';
      const cid = res?.conversation_id || resData?.conversation_id || null;

      setTestResult({ testUrl, responseText, conversation_id: cid });
      if (cid) { setConversationId(cid); setActiveConversationId(cid); }

    } catch (err: any) {
      const apiErrorMessage = err?.response?.data?.message;
      setError(apiErrorMessage || 'Failed to create test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (numQuestions) {
      setSelectedTime(String(parseInt(numQuestions) * 2));
    }
  }, [numQuestions]);

  const resetAll = () => {
    setSelectedSubject(''); setSelectedTopics([]); setSelectedSubtopics([]);
    setTestLevel('easy'); setTestType(''); setTestResult(null); setError('');
    setConversationId(null); setActiveConversationId(null); setActiveTest(null);
    setViewMode('browse'); setStep('testType');
    if (isSuperuser) {
      setSelectedBoard('');
      setSuperuserSelectedClass('');
      setSelectedOrganisation(null);
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('subject');
        return next;
      });
    }
  };

  const goBack = () => {
    if (step === 'topic') { setStep('testType'); setSelectedTopics([]); setSelectedSubtopics([]); }
    else if (step === 'subtopic') { setStep('topic'); setSelectedSubtopics([]); }
    else if (step === 'level') {
      if (testType === 'fullscale') setStep('testType');
      else if (testType === 'topic') setStep('topic');
      else setStep('subtopic');
    }
  };

  const sidebarEl = (
    <Sidebar
      selectedSubject={selectedSubject} selectedClass={effectiveClass}
      actions={actions} theme={theme} userId={userId} mobile={mobile}
      activeConversationId={activeConversationId} activeTestId={activeTest?._id || null}
      onTestClick={handleTestClick} onConvClick={handleConvClick}
    />
  );

  // ─── Wizard ────────────────────────────────────────────────────────────────
  const renderWizardStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: 2 } }}>

        {step === 'testType' && (
          <Fade in timeout={220}><Box>
            <Typography fontWeight={700} fontSize={13} color="#111827" mb={0.25}>Choose Test Type</Typography>
            <Typography fontSize={11} color="text.secondary" mb={1.5}>Different types have different selection rules</Typography>
            <Grid container spacing={1}>
              {WIZARD_TEST_TYPES.map(t => (
                <Grid item xs={12} sm={6} key={t.id}>
                  <TestTypeCard type={t} selected={testType === t.id} onClick={() => handleTestTypeSelect(t.id)} />
                </Grid>
              ))}
            </Grid>
            {testType && (
              <Button variant="contained" size="small" onClick={() => testType === 'fullscale' ? setStep('level') : setStep('topic')}
                sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, borderRadius: 2, fontSize: 12 }}>
                {testType === 'fullscale' ? 'Next: Difficulty →' : 'Next: Topic →'}
              </Button>
            )}
          </Box></Fade>
        )}

        {step === 'topic' && (
          <Fade in timeout={220}><Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IconButton size="small" onClick={goBack} sx={{ bgcolor: '#f1f5f9', p: 0.4 }}><ArrowBackIcon sx={{ fontSize: 14 }} /></IconButton>
              <Box>
                <Typography fontWeight={700} fontSize={13} color="#111827">{testType === 'custom' ? 'Select Topics (multiple)' : 'Select One Topic'}</Typography>
                <Chip size="small" label={testType === 'custom' ? '🟡 Multi-select' : '🔵 Single select'}
                  sx={{ height: 16, fontSize: 9, fontWeight: 600, mt: 0.25, bgcolor: testType === 'custom' ? '#f59e0b18' : '#3b82f618', color: testType === 'custom' ? '#d97706' : '#1d4ed8' }} />
              </Box>
            </Box>
            {availableTopics.length === 0 ? <Alert severity="info" sx={{ borderRadius: 1.5, fontSize: 11 }}>No topics found</Alert>
              : <ChipPicker items={availableTopics} selected={selectedTopics} onToggle={handleTopicToggle} />}
            {selectedTopics.length > 0 && (
              <Button variant="contained" size="small" onClick={() => testType === 'topic' ? setStep('level') : setStep('subtopic')}
                sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, borderRadius: 2, fontSize: 12 }}>
                {testType === 'topic' ? 'Next: Difficulty →' : 'Next: Subtopic →'}
              </Button>
            )}
          </Box></Fade>
        )}

        {step === 'subtopic' && (
          <Fade in timeout={220}><Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IconButton size="small" onClick={goBack} sx={{ bgcolor: '#f1f5f9', p: 0.4 }}><ArrowBackIcon sx={{ fontSize: 14 }} /></IconButton>
              <Box>
                <Typography fontWeight={700} fontSize={13} color="#111827">{testType === 'subtopic' ? 'Select One Subtopic' : 'Select Subtopics'}</Typography>
                <Chip size="small" label={testType === 'subtopic' ? '🔵 Single select' : '🟡 Multi-select'}
                  sx={{ height: 16, fontSize: 9, fontWeight: 600, mt: 0.25, bgcolor: testType === 'custom' ? '#f59e0b18' : '#10b98118', color: testType === 'custom' ? '#d97706' : '#059669' }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mb: 1 }}>
              {selectedTopics.map((t, i) => (
                <Chip key={t} label={t} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: `${CHIP_COLORS[i % CHIP_COLORS.length]}14`, color: CHIP_COLORS[i % CHIP_COLORS.length] }} />
              ))}
            </Box>
            {availableSubtopics.length === 0 ? <Alert severity="info" sx={{ borderRadius: 1.5, fontSize: 11 }}>No subtopics found</Alert>
              : <ChipPicker items={availableSubtopics} selected={selectedSubtopics} onToggle={handleSubtopicToggle} colorOff={2} />}
            <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap' }}>
              {(selectedSubtopics.length > 0 || testType === 'custom') && (
                <Button variant="contained" size="small" onClick={() => setStep('level')}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, fontSize: 12 }}>Next: Difficulty →</Button>
              )}
              {testType === 'custom' && selectedSubtopics.length === 0 && (
                <Button variant="outlined" size="small" onClick={() => setStep('level')}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: 11 }}>Skip</Button>
              )}
            </Box>
          </Box></Fade>
        )}

        {step === 'level' && (
          <Fade in timeout={220}><Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IconButton size="small" onClick={goBack} sx={{ bgcolor: '#f1f5f9', p: 0.4 }}><ArrowBackIcon sx={{ fontSize: 14 }} /></IconButton>
              <Typography fontWeight={700} fontSize={13} color="#111827">Difficulty & Generate</Typography>
            </Box>
            {/* Summary */}
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e5e7eb', mb: 1.5 }}>
              <Typography fontSize={10} fontWeight={700} color="#374151" mb={0.6}>📋 Test Config</Typography>
              <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
                <Chip label={`${getSCfg(selectedSubject).icon} ${selectedSubject}`} size="small"
                  sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: `${getSCfg(selectedSubject).color}14`, color: getSCfg(selectedSubject).color }} />
                {/* Show dynamically resolved class in test config summary */}
                {effectiveClass && (
                  <Chip label={`Class ${effectiveClass}`} size="small"
                    sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: '#f1f5f9', color: '#6b7280' }} />
                )}
                <Chip label={WIZARD_TEST_TYPES.find(t => t.id === testType)?.label || testType} size="small"
                  sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: '#6366f114', color: '#6366f1' }} />
                {testType === 'fullscale'
                  ? <Chip label={`All ${availableTopics.length} topics`} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: '#f1f5f9', color: '#6b7280' }} />
                  : <>
                    {selectedTopics.length > 0 && <Chip label={selectedTopics.length > 2 ? `${selectedTopics.length} topics` : `📌 ${selectedTopics.join(', ')}`} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: '#8b5cf614', color: '#8b5cf6' }} />}
                    {selectedSubtopics.length > 0 && <Chip label={selectedSubtopics.length > 2 ? `${selectedSubtopics.length} subtopics` : `🎯 ${selectedSubtopics.join(', ')}`} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: '#10b98114', color: '#10b981' }} />}
                    {testType === 'topic' && <Chip label="All subtopics" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: '#f1f5f9', color: '#6b7280' }} />}
                  </>
                }
              </Box>
            </Box>
            <Typography fontSize={11} fontWeight={700} color="#374151" mb={0.75}>Difficulty</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
              {Object.entries(LEVELS).map(([key, val]) => (
                <Box key={key} onClick={() => setTestLevel(key)}
                  sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                    py: 0.75, borderRadius: 1.5, cursor: 'pointer', position: 'relative',
                    border: `2px solid ${testLevel === key ? val.color : '#e5e7eb'}`,
                    bgcolor: testLevel === key ? `${val.color}10` : '#fafafa', transition: 'all 0.15s ease'
                  }}>
                  {testLevel === key && <CheckCircleIcon sx={{ position: 'absolute', top: 3, right: 3, fontSize: 11, color: val.color }} />}
                  <Typography fontSize={13}>{val.emoji}</Typography>
                  <Typography fontWeight={testLevel === key ? 700 : 500} fontSize={11} color={testLevel === key ? val.color : '#374151'}>{val.label}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ flexShrink: 0, pt: 1, pb: 2, borderTop: '1px solid #e5e7eb' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography fontWeight={700} fontSize={12} color="#374151">
                  Number of Questions
                </Typography>
                {/* <Typography fontWeight={700} fontSize={12} color="#374151">
                  Select Time
                </Typography> */}
                {numQuestions && (
                  <Typography fontSize={11} fontWeight={700} color={theme.palette.primary.main}>
                    {parseInt(numQuestions) * 2} min
                  </Typography>
                )}
              </Box>
              <QuestionCountSelector
                counts={QUESTION_COUNTS}
                selectedValue={numQuestions}
                unitLabel="questions"
                onChange={(id) => setNumQuestions(id)}
              />
            </Box>
            {/* <Box sx={{ flexShrink: 0, pt: 1, pb: 2, borderTop: '1px solid #e5e7eb' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontWeight={700} fontSize={12} color="#374151">
                  Select Time
                </Typography>
                {numQuestions && (
                  <Typography fontSize={11} fontWeight={700} color={theme.palette.primary.main}>
                    {parseInt(numQuestions) * 2} min
                  </Typography>
                )}
              </Box>
            </Box> */}
            {error && <Alert severity="error" sx={{ mb: 1, borderRadius: 1.5, fontSize: 11, py: 0.5 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" size="small" onClick={handleCreateTest}
                disabled={loading || !effectiveClass}
                startIcon={loading ? <CircularProgress size={12} color="inherit" /> : <QuizIcon sx={{ fontSize: 13 }} />}
                sx={{
                  textTransform: 'none', fontWeight: 700, fontSize: 11, px: 1.25, py: 0.4, borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': { filter: 'brightness(0.93)' }
                }}>
                {loading ? 'Generating...' : !effectiveClass ? 'Loading class info…' : '🚀 Generate Test'}
              </Button>
            </Box>
          </Box></Fade>
        )}

        {/* Result */}
        {testResult && (testResult.responseText || testResult.testUrl) && (
          <Fade in timeout={500}>
            <Paper elevation={0} sx={{ mt: 1.5, p: 1.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}10, #10b98112)`, border: `2px solid ${theme.palette.primary.main}30`, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 30, height: 30 }}><QuizIcon sx={{ fontSize: 16 }} /></Avatar>
                <Box>
                  <Typography fontWeight={800} color="primary" fontSize={13}>Created Successfully 🎉</Typography>
                  <Typography fontSize={10} color="text.secondary">Your test is ready</Typography>
                </Box>
              </Box>
              {testResult.responseText && (
                <Paper variant="outlined" sx={{ p: 1.25, mb: 1, bgcolor: '#f9fafb', borderRadius: 1.5, whiteSpace: 'pre-line' }}>
                  <Typography fontSize={11} color="#374151">{testResult?.responseText}</Typography>
                </Paper>
              )}
              {testResult.testUrl && (
                <Box sx={{ p: 1.25, bgcolor: '#fff', borderRadius: 1.5, border: '1.5px solid #e5e7eb' }}>
                  <Typography fontSize={10} fontWeight={700} mb={0.4}>Test Link:</Typography>
                  <Typography component="a" href={testResult.testUrl} target="_blank" rel="noopener noreferrer"
                    sx={{ fontSize: 11, color: theme.palette.primary.main, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 0.4, wordBreak: 'break-all', fontWeight: 600 }}>
                    {testResult.testUrl}<LinkIcon sx={{ fontSize: 12 }} />
                  </Typography>
                </Box>
              )}
            </Paper>
          </Fade>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{
        px: { xs: 1.5, sm: 2.5 }, py: 1,
        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexShrink: 0, boxShadow: '0 2px 10px rgba(0,0,0,0.15)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && <IconButton onClick={() => setSidebarOpen(true)} sx={{ color: '#fff', p: 0.5 }}><MenuIcon sx={{ fontSize: 20 }} /></IconButton>}
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 30, height: 30 }}><QuizIcon sx={{ fontSize: 16 }} /></Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography fontWeight={800} fontSize={{ xs: 13, sm: 16 }} letterSpacing={-0.3}>Practice Test</Typography>
              {/* Show dynamically resolved class in page header — same pattern as DoubtAssistantPage */}
              {/* {selectedClass && (
                <Typography sx={{ fontSize: { xs: 10, md: 11 }, color: '#fff', px: 1, py: 0.2, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)' }}>
                  Class {selectedClass}
                </Typography>
              )} */}
            </Box>
            <Typography fontSize={{ xs: 9, sm: 11 }} sx={{ opacity: 0.75, display: { xs: 'none', sm: 'block' } }}>{selectedSubject || 'Select subject'} → browse or create tests</Typography>
          </Box>
        </Box>
        {effectiveClass && (
          <Typography sx={{ fontSize: { xs: 10, md: 11 }, color: '#fff', px: 1, py: 0.2, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.15)' }}>
            Class {effectiveClass}
          </Typography>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {/* Mobile Drawer */}
        <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)} sx={{ '& .MuiDrawer-paper': { width: 285 } }}>
          <Box sx={{ p: '6px 6px 0', display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={() => setSidebarOpen(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>{sidebarEl}</Box>
        </Drawer>

        {/* Desktop Sidebar */}
        {!isMobile && (
          <Box sx={{ width: 290, flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', borderRight: '1px solid #e5e7eb', bgcolor: '#fff' }}>
            {sidebarEl}
          </Box>
        )}

        {/* Main Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: { xs: 1.25, sm: 1.75 }, gap: 1.5 }}>
          {/* Subjects */}
          <Box sx={{ flexShrink: 0 }}>
            {isSuperuser && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                  <Box>
                    <Typography fontWeight={800} fontSize={13} color="#111827">Context</Typography>
                    <Typography fontSize={11} color="text.secondary">Choose organisation, board, class and subject</Typography>
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: 260 }, ml: { sm: 'auto' } }}>
                    <OrganisationDropdown
                      label="Select Organisation"
                      value={selectedOrganisation}
                      onChange={handleOrganisationChange}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                  {selectedOrganisation && (
                    <Box>
                      <Typography fontSize={11} fontWeight={700} color="#374151" mb={0.4}>Board</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                        {boardOptions.length > 0 ? boardOptions.map((board) => (
                          <Chip
                            key={board.value}
                            label={board.label}
                            clickable
                            color={selectedBoard === board.value ? 'primary' : 'default'}
                            variant={selectedBoard === board.value ? 'filled' : 'outlined'}
                            onClick={() => handleBoardSelect(board.value)}
                            sx={{ fontWeight: 600, fontSize: 11 }}
                          />
                        )) : <Typography fontSize={10} color="text.secondary">No boards available for this organisation</Typography>}
                      </Box>
                    </Box>
                  )}
                  {selectedBoard && (
                    <Box>
                      <Typography fontSize={11} fontWeight={700} color="#374151" mb={0.4}>Class</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                        {classOptions.length > 0 ? classOptions.map((cls) => (
                          <Chip
                            key={cls.value}
                            label={cls.label}
                            clickable
                            color={superuserSelectedClass === cls.value ? 'primary' : 'default'}
                            variant={superuserSelectedClass === cls.value ? 'filled' : 'outlined'}
                            onClick={() => handleClassSelect(cls.value)}
                            sx={{ fontWeight: 600, fontSize: 11 }}
                          />
                        )) : <Typography fontSize={10} color="text.secondary">No classes available for this board</Typography>}
                      </Box>
                    </Box>
                  )}
                  {superuserSelectedClass && (
                    <Box>
                      <Typography fontSize={11} fontWeight={700} color="#374151" mb={0.4}>Subject</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                        {activeSubjects.length > 0 ? activeSubjects.map((subject) => (
                          <Chip
                            key={subject.value}
                            label={subject.value}
                            clickable
                            color={selectedSubject === subject.value ? 'primary' : 'default'}
                            variant={selectedSubject === subject.value ? 'filled' : 'outlined'}
                            onClick={() => handleSubjectSelect(subject.value)}
                            sx={{ fontWeight: 600, fontSize: 11 }}
                          />
                        )) : <Typography fontSize={10} color="text.secondary">No subjects available for this class</Typography>}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            {!isSuperuser && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: theme.palette.primary.main }} />
                  <Typography fontWeight={800} fontSize={13} color="#111827">Subjects</Typography>
                </Box>
                {subjectsLoading ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>{Array.from({ length: 6 }).map((_, i) => <Box key={i} sx={{ width: 70, height: 72, borderRadius: 2, bgcolor: '#f1f5f9' }} />)}</Box>
                ) : (
                  <Grid container spacing={1}>
                    {activeSubjects.map(s => (
                      <Grid item key={s.value} xs={4} sm={3} md={2} lg={2}>
                        <SubjectCard subject={s.value} selected={selectedSubject === s.value} onClick={() => handleSubjectSelect(s.value)} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Box>

          {/* Content area */}
          {selectedSubject && (
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: viewMode === 'custom' ? theme.palette.primary.main : viewMode === 'testDetail' ? getSCfg(selectedSubject).color : '#10b981' }} />
                  <Typography fontWeight={800} fontSize={13} color="#111827">
                    {viewMode === 'custom' ? 'Create Custom Test' : viewMode === 'testDetail' ? 'Test Details' : 'Browse & Start'}
                  </Typography>
                </Box>
                {viewMode !== 'custom' ? (
                  <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                    onClick={() => { setViewMode('custom'); setStep('testType'); setTestType(''); setSelectedTopics([]); setSelectedSubtopics([]); setTestResult(null); setActiveTest(null); }}
                    sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11, px: 1.25, py: 0.4, borderRadius: 2, borderWidth: 1.5 }}>
                    Custom Test
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" startIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                    onClick={() => { setViewMode('browse'); setTestType(''); setSelectedTopics([]); setSelectedSubtopics([]); setTestResult(null); setStep('testType'); }}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 11, px: 1, py: 0.3, borderRadius: 2, color: '#6b7280', borderColor: '#d1d5db' }}>
                    Browse
                  </Button>
                )}
              </Box>

              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {viewMode === 'browse' && (
                  <Box sx={{ height: '100%', overflowY: 'auto', '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: 2 } }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Box sx={{ width: 3, height: 14, borderRadius: 2, bgcolor: getSCfg(selectedSubject).color }} />
                        <Typography fontWeight={700} fontSize={12} color="#374151">Available Tests</Typography>
                        {browseLoading && <CircularProgress size={10} thickness={5} />}
                        {!browseLoading && browseTests.length > 0 && (
                          <Chip label={browseTests.length} size="small"
                            sx={{ height: 16, fontSize: 9, fontWeight: 800, bgcolor: `${getSCfg(selectedSubject).color}15`, color: getSCfg(selectedSubject).color, '& .MuiChip-label': { px: 0.75 } }} />
                        )}
                      </Box>

                      {browseLoading && (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          {[1, 2, 3].map(i => <Box key={i} sx={{ width: 220, height: 90, borderRadius: 2, bgcolor: '#f1f5f9', flexShrink: 0 }} />)}
                        </Box>
                      )}

                      {!browseLoading && browseTests.length === 0 && (
                        <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: '#9ca3af' }}>
                          <Box sx={{ fontSize: 32 }}>📋</Box>
                          <Typography fontSize={13} fontWeight={600} color="#374151">No available tests</Typography>
                          <Typography fontSize={11} textAlign="center">Use the sidebar to browse by type, or create a custom AI test above.</Typography>
                          {isMobile && (
                            <Button variant="outlined" size="small" startIcon={<MenuIcon sx={{ fontSize: 14 }} />}
                              onClick={() => setSidebarOpen(true)}
                              sx={{ mt: 0.5, textTransform: 'none', fontWeight: 700, fontSize: 12, borderRadius: 2 }}>
                              Open Test Library
                            </Button>
                          )}
                        </Box>
                      )}

                      {!browseLoading && browseTests.length > 0 && (
                        <Grid container spacing={1.5}>
                          {browseTests.map(test => {
                            const cfg = getSCfg(test.subject);
                            const diff = getDiffLabel(test.difficulty_distribution);
                            const isCustomMode = test.test_mode === 'custom';
                            const isActive = activeTest?._id === test._id;
                            return (
                              <Grid item xs={12} sm={6} md={4} key={test._id}>
                                <Box onClick={() => handleTestClick(test)}
                                  sx={{
                                    p: 1.5, borderRadius: 2, cursor: 'pointer', height: '100%',
                                    bgcolor: isActive ? `${cfg.color}10` : isCustomMode ? '#fffbeb' : '#fff',
                                    border: `1.5px solid ${isActive ? cfg.color + '60' : isCustomMode ? '#fde68a' : '#e5e7eb'}`,
                                    boxShadow: isActive ? `0 2px 12px ${cfg.color}20` : '0 1px 4px rgba(0,0,0,0.06)',
                                    '&:hover': { border: `1.5px solid ${cfg.color}`, boxShadow: `0 4px 14px ${cfg.color}18`, transform: 'translateY(-1px)' },
                                    transition: 'all 0.14s ease', position: 'relative', overflow: 'hidden'
                                  }}>
                                  {isCustomMode && (
                                    <Box sx={{
                                      position: 'absolute', top: 0, right: 0, px: 0.75, py: 0.2,
                                      background: 'linear-gradient(135deg,#f59e0b,#d97706)', borderBottomLeftRadius: 8
                                    }}>
                                      <Typography fontSize={8} fontWeight={800} color="#fff" sx={{ letterSpacing: 0.3 }}>CUSTOM</Typography>
                                    </Box>
                                  )}
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                                    <Box sx={{
                                      width: 32, height: 32, borderRadius: 1.5, background: cfg.gradient,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0
                                    }}>
                                      {cfg.icon}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography fontSize={11} fontWeight={700} color={isActive ? cfg.color : '#111827'}
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                                        {test.test_type === 'TOPIC'
                                          ? test.topics_covered?.[0] || test.test_type
                                          : test.test_type === 'SUBTOPIC'
                                            ? test.subtopics_covered?.[0] || test.test_type
                                            : test.test_type}
                                      </Typography>
                                      <Typography fontSize={9} color="#9ca3af" fontFamily="monospace">{test.test_id}</Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Typography fontSize={9} color="#9ca3af" sx={{ ml: 'auto' }}>{test.total_questions}Q · {test.test_time}m</Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </Box>
                  </Box>
                )}

                {viewMode === 'testDetail' && activeTest && (
                  <TestDetailPanel test={activeTest} onBack={() => { setViewMode('browse'); setActiveTest(null); }} />
                )}

                {viewMode === 'custom' && renderWizardStep()}
              </Box>
            </Box>
          )}

          {!selectedSubject && !subjectsLoading && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ fontSize: 44, mb: 1 }}>🎓</Box>
              <Typography fontSize={15} fontWeight={700} color="#374151" mb={0.5}>Select a subject to get started</Typography>
              <Typography fontSize={12} color="#9ca3af">Choose from the cards above</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TestCreatorPage;